import { downloadFile, request } from './client';
import type { PlanOption, SubscriptionDetail } from './types';
import type {
  AdminAuditLog,
  AuditSeverity,
  ApiKey,
  ApiKeySecret,
  BillingAccount,
  BillingProfile,
  BillingSettings,
  CampaignList,
  CatalogRow,
  CatalogView,
  ComplianceProfile,
  CostCard,
  CostsSummary,
  MoneySummary,
  MoneySummaryFilter,
  PaymentListFilter,
  CreateEnvironmentBody,
  CreateJobBody,
  CreditModeResult,
  DlrStats,
  EnvironmentList,
  EnvironmentProbeResult,
  EnvironmentTenant,
  EnvironmentTenantList,
  EnvironmentView,
  ExtWebhookResponse,
  Footprint,
  FxRate,
  FxRateList,
  GatewayTestResult,
  ImportKeyList,
  ImportKeyView,
  Invoice,
  InvoiceDetail,
  InvoicePaymentLink,
  IssuedImportKey,
  JobList,
  JobView,
  LegacyConcept,
  ManualGrantResult,
  MessagingAccount,
  MessagingGrantResult,
  MessagingProvider,
  MessagingRoute,
  Offer,
  OfferRedemptionList,
  Overview,
  PackDetail,
  PackList,
  Payment,
  PaymentGateway,
  MessagePriceInput,
  MessagePrices,
  PriceBookDiff,
  PriceBookItem,
  PriceBookVersion,
  PriceBookVersionDetail,
  PriceExplanation,
  PriceOverride,
  PricingRule,
  Products,
  TenantCredits,
  Projection,
  ProjectionBody,
  ProviderPayment,
  ProviderSpend,
  Purchase,
  RatedUsage,
  RetentionOverride,
  RetentionPolicyList,
  RetentionPolicyUpdate,
  RetentionRun,
  RevealedPassword,
  SenderId,
  Series,
  SeriesMetric,
  UpdateEnvironmentBody,
  SetupCheckView,
  OwnerInvitationResend,
  RemovalDetail,
  AiLocalTariff,
  AiRateCard,
  AiRateCards,
  ClawbackShortfall,
  CostCardVocabulary,
  CurrencyTable,
  LedgerSummary,
  MetaPricingReport,
  ParkedEvent,
  ParkedEventReplay,
  RefundNeededPayment,
  SalesIqWhatsAppSummary,
  TenantWhatsAppPanel,
  WhatsAppConsumptions,
} from './opsTypes';

/**
 * The tenant-ops client: every call goes to the CONSOLE API (`/api/v1/controlplane/…`), never to a
 * platform service (CP2, D-CP2). Billing / messaging admin calls go through the console's allow-listed
 * proxies, which stamp the environment's internal key and the acting admin — this file never sets either.
 *
 * Money figures are never cached here: the pages read them through `liveQuery` (staleTime 0, gcTime 0).
 */

const enc = encodeURIComponent;

function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}

function post<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) });
}

function put<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'PUT', body: JSON.stringify(body) });
}

function del<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, body === undefined ? { method: 'DELETE' } : { method: 'DELETE', body: JSON.stringify(body) });
}

const envPath = (env: string) => `/environments/${enc(env)}`;
const billingPath = (env: string, path: string) => `${envPath(env)}/billing/${path}`;
const messagingPath = (env: string, path: string) => `${envPath(env)}/messaging/${path}`;
const aiPath = (env: string, path: string) => `${envPath(env)}/ai/${path}`;

/** Whole UTC days (inclusive) → the half-open instants every service reads: `[from 00:00Z, to+1 00:00Z)`. */
export function dayWindow(from: string, to: string): { from: string; to: string } {
  const next = new Date(`${to}T00:00:00Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  return { from: `${from}T00:00:00Z`, to: `${next.toISOString().slice(0, 10)}T00:00:00Z` };
}

// ─── Console-native tenant ops ─────────────────────────────────────────────

export const opsApi = {
  environments: {
    list: () => request<EnvironmentList>('/environments'),
    get: (key: string) => request<EnvironmentView>(`/environments/${enc(key)}`),
    create: (body: CreateEnvironmentBody) => post<EnvironmentView>('/environments', body),
    update: (key: string, body: UpdateEnvironmentBody) => put<EnvironmentView>(`/environments/${enc(key)}`, body),
    /** Write-only: the answer carries presence + time, never the key (CP4). */
    setKey: (key: string, internalApiKey: string) =>
      put<EnvironmentView>(`/environments/${enc(key)}/internal-api-key`, { internal_api_key: internalApiKey }),
    probe: (key: string) => post<EnvironmentProbeResult>(`/environments/${enc(key)}/probe`),
  },

  importKeys: {
    list: () => request<ImportKeyList>('/import-keys'),
    /** The ONE answer that carries the plaintext key. */
    create: (label: string, scope: string, environment?: string) =>
      post<IssuedImportKey>('/import-keys', { label, scope, environment }),
    revoke: (id: string) => post<ImportKeyView>(`/import-keys/${enc(id)}/revoke`),
  },

  provisioning: {
    createJob: (body: CreateJobBody) => post<JobView>('/provisioning/jobs', body),
    listJobs: (filter: { environment?: string; status?: string; limit?: number; cursor?: string | null }) =>
      request<JobList>(`/provisioning/jobs${qs({ ...filter })}`),
    getJob: (id: string) => request<JobView>(`/provisioning/jobs/${enc(id)}`),
    retryJob: (id: string) => post<JobView>(`/provisioning/jobs/${enc(id)}/retry`),
    retryStep: (id: string, stepKey: string) =>
      post<JobView>(`/provisioning/jobs/${enc(id)}/steps/${enc(stepKey)}/retry`),
    cancelJob: (id: string) => post<JobView>(`/provisioning/jobs/${enc(id)}/cancel`),
    /** Once: 410 PASSWORD_ALREADY_REVEALED afterwards. The caller must never cache the answer. */
    revealPassword: (id: string) => post<RevealedPassword>(`/provisioning/jobs/${enc(id)}/reveal-password`),
    catalog: (env: string) => request<CatalogView>(`/provisioning/catalog/${enc(env)}`),
    putCatalog: (env: string, steps: CatalogRow[]) => put<CatalogView>(`/provisioning/catalog/${enc(env)}`, { steps }),
    /** A deletion job's decisions and backup. */
    removal: (id: string) => request<RemovalDetail>(`/provisioning/jobs/${enc(id)}/removal`),
    /** The second owner's approval (422 SAME_ADMIN for the one who asked). */
    approveRemoval: (id: string) => post<RemovalDetail>(`/provisioning/jobs/${enc(id)}/approve`),
    /** The final confirmation after the verified backup: the tenant's short name, typed. */
    confirmRemoval: (id: string, dbShort: string) => post<RemovalDetail>(`/provisioning/jobs/${enc(id)}/confirm`, { db_short: dbShort }),
    /** Stops a deletion before anything irreversible; a frozen tenant is unfrozen first. */
    rejectRemoval: (id: string, reason: string) => post<JobView>(`/provisioning/jobs/${enc(id)}/reject`, { reason }),
  },

  packs: {
    list: () => request<PackList>('/industry-packs'),
    get: (key: string) => request<PackDetail>(`/industry-packs/${enc(key)}`),
  },

  tenants: {
    list: (env: string) => request<EnvironmentTenantList>(`${envPath(env)}/tenants`),
    get: (env: string, tenantId: string) => request<EnvironmentTenant>(`${envPath(env)}/tenants/${enc(tenantId)}`),
    footprint: (env: string, tenantId: string, days: number) =>
      request<Footprint>(`${envPath(env)}/tenants/${enc(tenantId)}/footprint${qs({ days })}`),
    /** LIVE — each panel independently ok / unavailable (CP2). */
    products: (env: string, tenantId: string, period?: string) =>
      request<Products>(`${envPath(env)}/tenants/${enc(tenantId)}/products${qs({ period })}`),
    /** LIVE, on demand — the setup job's parity checks re-run read-only; one verdict per service. */
    setupCheck: (env: string, tenantId: string) =>
      request<SetupCheckView>(`${envPath(env)}/tenants/${enc(tenantId)}/setup-check`),
    /** LIVE — the AI and SMS/WhatsApp wallets' current balances, each on its own (CP2). */
    credits: (env: string, tenantId: string) => request<TenantCredits>(`${envPath(env)}/tenants/${enc(tenantId)}/credits`),
    /** LIVE — salesIQ's WhatsApp debits for whole UTC days (money C8), as a panel. */
    whatsappSalesIqSummary: (env: string, tenantId: string, from: string, to: string) =>
      request<TenantWhatsAppPanel<SalesIqWhatsAppSummary>>(
        `${envPath(env)}/tenants/${enc(tenantId)}/whatsapp/salesiq-summary${qs({ from, to })}`),
    /** LIVE — Meta's recorded pricing beside our category (salesIQ, C8): a record, never a charge. */
    whatsappMetaPricing: (env: string, tenantId: string, from: string, to: string) =>
      request<TenantWhatsAppPanel<MetaPricingReport>>(
        `${envPath(env)}/tenants/${enc(tenantId)}/whatsapp/meta-pricing${qs({ from, to })}`),
    /** E-mails the owner a NEW invitation link (IAM's founder-invitation send; the previous link stops working). Owner only. */
    resendOwnerInvitation: (env: string, tenantId: string) =>
      post<OwnerInvitationResend>(`${envPath(env)}/tenants/${enc(tenantId)}/owner-invitation/resend`),
    /** Asks for the tenant's deletion; another owner must approve it. Answers the deletion job. */
    requestDeletion: (env: string, tenantId: string, reason: string) =>
      post<JobView>(`${envPath(env)}/tenants/${enc(tenantId)}/delete`, { reason }),
  },

  health: {
    overview: (env: string, hours: number) => request<Overview>(`${envPath(env)}/overview${qs({ hours })}`),
    series: (
      env: string,
      params: { metric: SeriesMetric; host?: string; container?: string; group?: string; mount?: string; hours: number },
    ) => request<Series>(`${envPath(env)}/series${qs({ ...params })}`),
  },

  /** The Billing-tab subscription operations, against THIS environment's IAM (§7). */
  subscription: {
    get: (env: string, tenantId: string) =>
      request<SubscriptionDetail>(`${envPath(env)}/accounts/${enc(tenantId)}/subscription`),
    plans: (env: string, tenantId: string) =>
      request<PlanOption[]>(`${envPath(env)}/accounts/${enc(tenantId)}/subscription/plans`),
    changePlan: (
      env: string,
      tenantId: string,
      body: { plan_code: string; billing_cycle?: string; price_minor?: number; currency?: string },
    ) => post<SubscriptionDetail>(`${envPath(env)}/accounts/${enc(tenantId)}/subscription/plan`, body),
    setSeats: (env: string, tenantId: string, seatLimit: number | null) =>
      post<SubscriptionDetail>(`${envPath(env)}/accounts/${enc(tenantId)}/subscription/seats`, { seat_limit: seatLimit }),
    extendTrial: (env: string, tenantId: string, days: number) =>
      post<SubscriptionDetail>(`${envPath(env)}/accounts/${enc(tenantId)}/subscription/extend-trial`, { days }),
    setOverride: (
      env: string,
      tenantId: string,
      body: { app_key: string; effect: 'grant' | 'deny'; reason?: string; expires_at?: string },
    ) => post<SubscriptionDetail>(`${envPath(env)}/accounts/${enc(tenantId)}/subscription/overrides`, body),
    clearOverride: (env: string, tenantId: string, appKey: string) =>
      del<SubscriptionDetail>(`${envPath(env)}/accounts/${enc(tenantId)}/subscription/overrides/${enc(appKey)}`),
    changeStatus: (env: string, tenantId: string, status: string, reason?: string) =>
      post<SubscriptionDetail>(`${envPath(env)}/accounts/${enc(tenantId)}/subscription/status`, { status, reason }),
  },
};

// ─── Billing admin (proxied: /environments/{env}/billing/{path}) ────────────

export const billingApi = {
  settings: (env: string) => request<BillingSettings>(billingPath(env, 'settings')),
  updateSettings: (env: string, body: Record<string, unknown> & { reason: string }) =>
    put<BillingSettings>(billingPath(env, 'settings'), body),

  fxRates: (env: string) => request<FxRateList>(billingPath(env, 'fx-rates')),
  addFxRate: (
    env: string,
    body: { currency: string; units_per_reference: number; effective_from?: string; needs_review?: boolean; note?: string; reason: string },
  ) => post<FxRate>(billingPath(env, 'fx-rates'), body),

  accounts: (env: string, filter: { kind?: string; status?: string; limit?: number; cursor?: string | null } = {}) =>
    request<{ accounts: BillingAccount[]; next_cursor: string | null }>(billingPath(env, `accounts${qs({ ...filter })}`)),

  profile: (env: string, tenantId: string) => request<BillingProfile>(billingPath(env, `profiles/${enc(tenantId)}`)),
  /** Full replacement — a null field clears its override; a currency / tax change needs a reason. */
  updateProfile: (env: string, tenantId: string, body: Record<string, unknown>) =>
    put<BillingProfile>(billingPath(env, `profiles/${enc(tenantId)}`), body),

  /** Billing's minor-unit digits per currency (C10) — money is formatted with it, never with Intl. */
  currencies: (env: string) => request<CurrencyTable>(billingPath(env, 'currencies')),

  costCards: (env: string, filter: { product?: string; provider_key?: string; country?: string; effective_only?: boolean } = {}) =>
    request<{ cost_cards: CostCard[] }>(billingPath(env, `cost-cards${qs({ ...filter })}`)),
  /** The units and categories each product's cost cards take (C10). */
  costCardVocabulary: (env: string) => request<CostCardVocabulary>(billingPath(env, 'cost-cards/vocabulary')),
  /** Always a NEW version row — cost cards are add-only. */
  addCostCard: (env: string, body: Record<string, unknown>) => post<CostCard>(billingPath(env, 'cost-cards'), body),

  pricingRules: (env: string) => request<{ pricing_rules: PricingRule[] }>(billingPath(env, 'pricing-rules')),
  updatePricingRule: (env: string, product: string, defaultMarkupBps: number, reason: string) =>
    put<PricingRule>(billingPath(env, `pricing-rules/${enc(product)}`), { default_markup_bps: defaultMarkupBps, reason }),

  versions: (env: string) => request<{ versions: PriceBookVersion[] }>(billingPath(env, 'price-book/versions')),
  version: (env: string, id: string) => request<PriceBookVersionDetail>(billingPath(env, `price-book/versions/${enc(id)}`)),
  createDraft: (env: string, body: { note?: string; copy_from_version_id?: string }) =>
    post<PriceBookVersionDetail>(billingPath(env, 'price-book/versions'), body),
  replaceItems: (env: string, id: string, prices: PriceBookItem[], reason: string) =>
    put<PriceBookVersionDetail>(billingPath(env, `price-book/versions/${enc(id)}/items`), { prices, reason }),
  generateConsumption: (env: string, id: string) =>
    post<{ version_id: string; generated: number; prices: PriceBookItem[] }>(
      billingPath(env, `price-book/versions/${enc(id)}/generate-consumption`)),
  publish: (env: string, id: string, effectiveFrom: string, reason: string) =>
    post<PriceBookVersionDetail>(billingPath(env, `price-book/versions/${enc(id)}/publish`), {
      effective_from: effectiveFrom,
      reason,
    }),
  diff: (env: string, from: string, to: string) =>
    request<PriceBookDiff>(billingPath(env, `price-book/diff${qs({ from, to })}`)),

  /** The price of each SMS / WhatsApp message type — the platform's, or one tenant's (its overrides applied). */
  messagePrices: (env: string, tenantId?: string) =>
    request<MessagePrices>(billingPath(env, `message-prices${qs({ tenant_id: tenantId })}`)),
  /**
   * Publishes a new price-book version now with these prices (micro-credits per part / message): `country` `*` (default)
   * or ISO-2; `price_credits: null` removes the book row (back to cost + markup). A price under our highest cost answers
   * 422 PRICE_BELOW_COST unless the item carries `allow_below_cost: true` (C10).
   */
  setMessagePrices: (env: string, prices: MessagePriceInput[], reason: string) =>
    put<MessagePrices>(billingPath(env, 'message-prices'), { prices, reason }),

  overrides: (env: string, tenantId?: string) =>
    request<{ overrides: PriceOverride[] }>(billingPath(env, `overrides${qs({ tenant_id: tenantId })}`)),
  createOverride: (env: string, body: Record<string, unknown>) => post<PriceOverride>(billingPath(env, 'overrides'), body),
  expireOverride: (env: string, id: string, reason: string) =>
    post<PriceOverride>(billingPath(env, `overrides/${enc(id)}/expire`), { reason }),

  offers: (env: string) => request<{ offers: Offer[] }>(billingPath(env, 'offers')),
  createOffer: (env: string, body: Record<string, unknown>) => post<Offer>(billingPath(env, 'offers'), body),
  expireOffer: (env: string, id: string, reason: string) => post<Offer>(billingPath(env, `offers/${enc(id)}/expire`), { reason }),
  redemptions: (env: string, id: string, cursor?: string | null) =>
    request<OfferRedemptionList>(billingPath(env, `offers/${enc(id)}/redemptions${qs({ cursor, limit: 100 })}`)),

  rating: (env: string, tenantId: string, period: string) =>
    request<RatedUsage>(billingPath(env, `rating/${enc(tenantId)}${qs({ period })}`)),
  project: (env: string, tenantId: string, body: ProjectionBody) =>
    post<Projection>(billingPath(env, `rating/${enc(tenantId)}/project`), body),
  explain: (env: string, tenantId: string, sku: string) =>
    request<PriceExplanation>(billingPath(env, `rating/${enc(tenantId)}/explain${qs({ sku })}`)),
  costsSummary: (env: string, period: string) => request<CostsSummary>(billingPath(env, `costs/summary${qs({ period })}`)),
  /** One tenant's ledger totals per feature for a product over `[from, to)` instants (C8). */
  ledgerSummary: (env: string, filter: { tenant_id: string; product: string; from: string; to: string }) =>
    request<LedgerSummary>(billingPath(env, `ledger/summary${qs({ ...filter })}`)),

  /** Ledger events billing parked (C10), newest first; `before` pages back. */
  parkedEvents: (env: string, filter: { source?: string; limit?: number; before?: string | null } = {}) =>
    request<{ events: ParkedEvent[] }>(billingPath(env, `parked-events${qs({ ...filter })}`)),
  replayParkedEvent: (env: string, id: string, reason: string) =>
    post<ParkedEventReplay>(billingPath(env, `parked-events/${enc(id)}/replay`), { reason }),
  /** Payments that must be refunded by hand at the gateway (C10). */
  paymentsNeedingRefund: (env: string) =>
    request<{ payments: RefundNeededPayment[] }>(billingPath(env, 'payments/needs-refund')),
  /** Refunded packs whose credits were already spent: what the clawback could not take back (C10). */
  clawbackShortfalls: (env: string) =>
    request<{ shortfalls: ClawbackShortfall[] }>(billingPath(env, 'clawback-shortfalls')),

  invoices: (env: string, filter: { tenant_id?: string; status?: string; kind?: string; cursor?: string | null } = {}) =>
    request<{ invoices: Invoice[]; next_cursor: string | null }>(billingPath(env, `invoices${qs({ ...filter, limit: 50 })}`)),
  invoice: (env: string, id: string) => request<InvoiceDetail>(billingPath(env, `invoices/${enc(id)}`)),
  /** Deterministic invoice ids — a re-run for the same period and tenant creates nothing new. */
  generateInvoices: (env: string, body: { period: string; tenant_id?: string; reason: string }) =>
    post<{ period: string; created: number; existing: number; skipped: number; invoice_ids: string[] }>(
      billingPath(env, 'invoices/generate'), body),
  invoicePdf: (env: string, id: string, fallbackName: string) =>
    downloadFile(billingPath(env, `invoices/${enc(id)}/pdf`), fallbackName),
  issueInvoice: (env: string, id: string, reason: string) =>
    post<Invoice>(billingPath(env, `invoices/${enc(id)}/issue`), { reason }),
  voidInvoice: (env: string, id: string, reason: string) =>
    post<Invoice>(billingPath(env, `invoices/${enc(id)}/void`), { reason }),
  markInvoicePaid: (env: string, id: string, reason: string) =>
    post<Invoice>(billingPath(env, `invoices/${enc(id)}/mark-paid`), { reason }),
  /**
   * A hosted checkout for an issued invoice (`POST invoices/{}/payment-link`, allow-listed for billing:manage). When
   * billing cannot make one (no gateway connected, say) it answers with its own code, which the UI shows verbatim.
   */
  invoicePaymentLink: (env: string, id: string) =>
    post<InvoicePaymentLink>(billingPath(env, `invoices/${enc(id)}/payment-link`), {}),

  /** `from` / `to` are ISO instants on created_at; `test_mode` filters by the paying gateway row's mode (2026-10-14). */
  payments: (env: string, filter: PaymentListFilter = {}) =>
    request<{ payments: Payment[]; next_cursor: string | null }>(billingPath(env, `payments${qs({ limit: 50, ...filter })}`)),
  purchases: (env: string, filter: { tenant_id?: string; status?: string; from?: string; to?: string; test_mode?: boolean; cursor?: string | null } = {}) =>
    request<{ purchases: Purchase[]; next_cursor: string | null }>(billingPath(env, `purchases${qs({ ...filter, limit: 50 })}`)),
  /** The Money dashboard (2026-10-14): one aggregate over every tenant for `[from, to)` — billing's, never summed here. */
  moneySummary: (env: string, filter: MoneySummaryFilter) =>
    request<MoneySummary>(billingPath(env, `money/summary${qs({ ...filter })}`)),
  retryGrant: (env: string, id: string, reason: string) =>
    post<Purchase>(billingPath(env, `purchases/${enc(id)}/retry-grant`), { reason }),
  /** A live, idempotent wallet grant (micro-credits). */
  grant: (
    env: string,
    body: { tenant_id: string; wallet: 'ai' | 'communications'; credits: number; reason: string; idempotency_key: string; account_id?: string },
  ) => post<ManualGrantResult>(billingPath(env, 'grants'), body),

  gateways: (env: string, filter: { owner_tenant_id?: string; scope?: 'platform' } = {}) =>
    request<{ gateways: PaymentGateway[] }>(billingPath(env, `gateways${qs({ ...filter })}`)),
  createGateway: (env: string, body: Record<string, unknown>) => post<PaymentGateway>(billingPath(env, 'gateways'), body),
  /** A secret omitted from `secrets` keeps its stored value — secrets are write-only. */
  updateGateway: (env: string, id: string, body: Record<string, unknown>) =>
    put<PaymentGateway>(billingPath(env, `gateways/${enc(id)}`), body),
  gatewayAction: (env: string, id: string, action: 'enable' | 'disable' | 'retire', reason: string) =>
    post<PaymentGateway>(billingPath(env, `gateways/${enc(id)}/${action}`), { reason }),
  testGateway: (env: string, id: string) => post<GatewayTestResult>(billingPath(env, `gateways/${enc(id)}/test`)),

  apiKeys: (env: string, tenantId: string) =>
    request<{ api_keys: ApiKey[] }>(billingPath(env, `api-keys${qs({ tenant_id: tenantId })}`)),
  /** The plaintext `key` is in THIS answer only. */
  createApiKey: (env: string, body: Record<string, unknown>) => post<ApiKeySecret>(billingPath(env, 'api-keys'), body),
  rotateApiKey: (env: string, id: string) => post<ApiKeySecret>(billingPath(env, `api-keys/${enc(id)}/rotate`)),
  revokeApiKey: (env: string, id: string, reason: string) =>
    post<ApiKey>(billingPath(env, `api-keys/${enc(id)}/revoke`), { reason }),

  extWebhook: (env: string, tenantId: string) => request<ExtWebhookResponse>(billingPath(env, `ext-webhooks/${enc(tenantId)}`)),
  putExtWebhook: (
    env: string,
    tenantId: string,
    body: { url?: string; events?: string[]; enabled?: boolean; rotate_secret?: boolean; reason: string },
  ) => put<ExtWebhookResponse>(billingPath(env, `ext-webhooks/${enc(tenantId)}`), body),

  retentionPolicies: (env: string) => request<RetentionPolicyList>(billingPath(env, 'retention/policies')),
  /** Days and/or the enforcement switch per class; billing requires the reason (422 REASON_REQUIRED) and audits it. */
  updateRetentionPolicies: (env: string, policies: RetentionPolicyUpdate[], reason: string) =>
    put<RetentionPolicyList>(billingPath(env, 'retention/policies'), { policies, reason }),
  retentionOverrides: (env: string, tenantId: string) =>
    request<{ tenant_id: string; overrides: RetentionOverride[] }>(billingPath(env, `retention/overrides/${enc(tenantId)}`)),
  setRetentionOverride: (env: string, tenantId: string, dataClass: string, days: number, reason: string) =>
    put<RetentionOverride>(billingPath(env, `retention/overrides/${enc(tenantId)}/${enc(dataClass)}`), { days, reason }),
  /**
   * The console proxy forwards no body on a DELETE, so the (required) reason rides `?reason=` — billing
   * reads it from the query when the body is absent.
   */
  deleteRetentionOverride: (env: string, tenantId: string, dataClass: string, reason: string) =>
    del<void>(billingPath(env, `retention/overrides/${enc(tenantId)}/${enc(dataClass)}${qs({ reason })}`)),
  retentionRuns: (env: string, filter: { data_class?: string; tenant_id?: string; cursor?: string | null } = {}) =>
    request<{ runs: RetentionRun[]; next_cursor: string | null }>(billingPath(env, `retention/runs${qs({ ...filter, limit: 50 })}`)),

  auditLog: (env: string, filter: { tenant_id?: string; action?: string; target_type?: string; severity?: AuditSeverity; cursor?: string | null } = {}) =>
    request<AdminAuditLog>(billingPath(env, `audit-log${qs({ ...filter, limit: 50 })}`)),
};

// ─── Messaging admin (proxied: /environments/{env}/messaging/{path}) ───────

export const messagingApi = {
  providers: (env: string) => request<{ providers: MessagingProvider[] }>(messagingPath(env, 'providers')),
  createProvider: (env: string, body: Record<string, unknown>) =>
    post<MessagingProvider>(messagingPath(env, 'providers'), body),
  /** `credentials` omitted = keep the stored (encrypted) value — write-only (MS5). */
  updateProvider: (env: string, key: string, body: Record<string, unknown>) =>
    put<MessagingProvider>(messagingPath(env, `providers/${enc(key)}`), body),
  testProvider: (env: string, key: string) =>
    post<{ key: string; ok: boolean; detail: string }>(messagingPath(env, `providers/${enc(key)}/test`)),
  providerBalance: (env: string, key: string) =>
    request<{ key: string; supported: boolean; balance_minor: number | null; currency: string | null; detail: string | null }>(
      messagingPath(env, `providers/${enc(key)}/balance`)),

  routes: (env: string) => request<{ routes: MessagingRoute[] }>(messagingPath(env, 'routes')),
  createRoute: (env: string, body: Record<string, unknown>) => post<MessagingRoute>(messagingPath(env, 'routes'), body),
  updateRoute: (env: string, id: string, body: Record<string, unknown>) =>
    put<MessagingRoute>(messagingPath(env, `routes/${enc(id)}`), body),
  deleteRoute: (env: string, id: string) => del<void>(messagingPath(env, `routes/${enc(id)}`)),

  complianceProfiles: (env: string) => request<{ profiles: ComplianceProfile[] }>(messagingPath(env, 'compliance-profiles')),
  putComplianceProfile: (env: string, country: string, body: Record<string, unknown>) =>
    put<ComplianceProfile>(messagingPath(env, `compliance-profiles/${enc(country)}`), body),

  legacyConcepts: (env: string) => request<{ concepts: LegacyConcept[] }>(messagingPath(env, 'legacy-concepts')),
  createLegacyConcept: (env: string, body: Record<string, unknown>) =>
    post<LegacyConcept>(messagingPath(env, 'legacy-concepts'), body),
  updateLegacyConcept: (env: string, concept: string, body: Record<string, unknown>) =>
    put<LegacyConcept>(messagingPath(env, `legacy-concepts/${enc(concept)}`), body),

  senderIds: (env: string, filter: { tenant_id?: string; status?: string } = {}) =>
    request<{ sender_ids: SenderId[] }>(messagingPath(env, `sender-ids${qs({ ...filter })}`)),
  createSenderId: (env: string, body: Record<string, unknown>) => post<SenderId>(messagingPath(env, 'sender-ids'), body),
  transitionSenderId: (
    env: string,
    id: string,
    transition: 'submit' | 'approve' | 'reject' | 'suspend',
    body: { reason?: string; documents?: unknown },
  ) => post<SenderId>(messagingPath(env, `sender-ids/${enc(id)}/${transition}`), body),

  providerSpend: (env: string, filter: { provider?: string; from?: string; to?: string } = {}) =>
    request<ProviderSpend>(messagingPath(env, `provider-spend${qs({ ...filter })}`)),
  recordProviderPayment: (env: string, body: Record<string, unknown>) =>
    post<ProviderPayment>(messagingPath(env, 'provider-payments'), body),

  dlrStats: (env: string, filter: { from?: string; to?: string } = {}) =>
    request<DlrStats>(messagingPath(env, `dlr-stats${qs({ ...filter })}`)),

  /** ONE tenant per call — the proxy refuses a call without `tenant_id` (no cross-tenant enumeration). */
  campaigns: (env: string, tenantId: string, filter: { status?: string; cursor?: string | null } = {}) =>
    request<CampaignList>(messagingPath(env, `campaigns${qs({ tenant_id: tenantId, ...filter, limit: 50 })}`)),

  accounts: (env: string, tenantId: string) =>
    request<{ accounts: MessagingAccount[] }>(messagingPath(env, `tenants/${enc(tenantId)}/accounts`)),
  grant: (
    env: string,
    tenantId: string,
    body: { amount_credits: number; reason: string; idempotency_key: string; account_id?: string },
  ) => post<MessagingGrantResult>(messagingPath(env, `tenants/${enc(tenantId)}/grants`), body),
  setCreditMode: (env: string, tenantId: string, creditMode: 'prepaid' | 'postpaid', reason: string) =>
    put<CreditModeResult>(messagingPath(env, `tenants/${enc(tenantId)}/credit-mode`), { credit_mode: creditMode, reason }),
  /** The tenant's WhatsApp lease consumptions over `[from, to)` instants (money C8). */
  whatsappConsumptions: (env: string, tenantId: string, from: string, to: string) =>
    request<WhatsAppConsumptions>(messagingPath(env, `tenants/${enc(tenantId)}/whatsapp/consumptions${qs({ from, to })}`)),

  auditLog: (env: string, filter: { tenant_id?: string; cursor?: string | null } = {}) =>
    request<AdminAuditLog>(messagingPath(env, `audit-log${qs({ ...filter, limit: 50 })}`)),
};

// ─── AI admin (proxied: /environments/{env}/ai/{path}, money review C6) ─────

export const aiApi = {
  /** Every platform rate card and local tariff, active and deactivated (costs:read). */
  rateCards: (env: string) => request<AiRateCards>(aiPath(env, 'rate-cards')),
  /** The new active version for (provider, model); the previous active row is deactivated with it. Reason required. */
  addRateCard: (
    env: string,
    body: {
      provider_key: string;
      model_key: string;
      unit: string;
      input_cost_per_million_usd: number;
      output_cost_per_million_usd: number;
      margin_bps: number;
      min_charge_credits: number;
      reason: string;
    },
  ) => post<AiRateCard>(aiPath(env, 'rate-cards'), body),
  deactivateRateCard: (env: string, id: string, reason: string) =>
    post<AiRateCard>(aiPath(env, `rate-cards/${enc(id)}/deactivate`), { reason }),
  addLocalTariff: (
    env: string,
    body: { task: string; unit: string; credits_per_unit: number; min_charge_credits: number; reason: string },
  ) => post<AiLocalTariff>(aiPath(env, 'local-tariffs'), body),
  deactivateLocalTariff: (env: string, id: string, reason: string) =>
    post<AiLocalTariff>(aiPath(env, `local-tariffs/${enc(id)}/deactivate`), { reason }),
};
